/**
 * UnifiedPreferencesWizard — Phase UX-E
 * Behind ENABLE_PREFERENCES_WIZARD_V2 and ENABLE_ONBOARDING_WIZARD_V2
 *
 * A unified wizard component for BOTH onboarding and editing digest profiles.
 * Supports 5 steps: Specialty → Subspecialties → Journals → Topics → Schedule
 *
 * Props:
 * - mode: "onboarding" | "edit"
 * - profile: existing profile to edit (null for onboarding/create)
 * - onComplete: callback when wizard completes
 * - onCancel: callback when wizard is cancelled
 *
 * PHI-Zero: All custom text inputs are scanned for PHI before submission.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { configAPI, preferencesAPI, digestsAPI, libraryAPI } from '../api';
import { toast } from 'sonner';
import { trackOnboarding } from '../utils/analytics';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { getStarterPack, hasStarterPack } from '../config/starterPacks';
import {
  ChevronRight, ChevronLeft, Check, X, Clock, Calendar,
  BookOpen, FileText, Settings, ChevronDown, ChevronUp, Loader2,
  Stethoscope, Building2, Star, Sparkles, Search, GraduationCap,
  Play, PartyPopper, ArrowRight, Mail
} from 'lucide-react';

// Fixed suppress options
const SUPPRESS_OPTIONS = [
  { value: '', label: 'No suppression' },
  { value: '1', label: '1 day' },
  { value: '3', label: '3 days' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '28', label: '28 days' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Custom interval options for "Every X [unit]" selector
const INTERVAL_VALUES = Array.from({ length: 7 }, (_, i) => i + 1);
const INTERVAL_UNITS = [
  { value: 'days', label: 'Day(s)' },
  { value: 'weeks', label: 'Week(s)' },
];

// Time picker helpers
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const AM_PM = ['AM', 'PM'];

function parseTimeTo12h(time24: string): { hour: number; minute: string; ampm: string } {
  const [h, m] = time24.split(':').map(Number);
  const minute = String(m < 15 ? 0 : m < 30 ? 15 : m < 45 ? 30 : 45).padStart(2, '0');
  if (h === 0) return { hour: 12, minute, ampm: 'AM' };
  if (h < 12) return { hour: h, minute, ampm: 'AM' };
  if (h === 12) return { hour: 12, minute, ampm: 'PM' };
  return { hour: h - 12, minute, ampm: 'PM' };
}

function to24h(hour: number, minute: string, ampm: string): string {
  let h24 = hour;
  if (ampm === 'AM' && hour === 12) h24 = 0;
  else if (ampm === 'PM' && hour !== 12) h24 = hour + 12;
  return `${String(h24).padStart(2, '0')}:${minute}`;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const STEP_LABELS = ['Specialty', 'Subspecialties', 'Journals', 'Topics', 'Schedule', 'Complete'];
const TOTAL_STEPS = 6;
const MAX_SUBSPECIALTIES = 3;

// Digest limits by user tier
const DIGEST_LIMITS = {
  pro: 5,
  verified: 3,
  free: 1,
};

const getUserTierLabel = (user: any): { tier: 'pro' | 'verified' | 'free'; label: string; limit: number } => {
  // Check if user has premium or active trial
  const isPremium = user?.plan_tier === 'premium';
  const hasActiveTrial = user?.trial_ends_at && new Date(user.trial_ends_at) > new Date();
  
  if (isPremium || hasActiveTrial) {
    return { tier: 'pro', label: 'Pro', limit: DIGEST_LIMITS.pro };
  }
  
  // Check if user is verified
  const isVerified = user?.peer_verification_status === 'verified';
  if (isVerified) {
    return { tier: 'verified', label: 'Verified', limit: DIGEST_LIMITS.verified };
  }
  
  return { tier: 'free', label: 'Free', limit: DIGEST_LIMITS.free };
};

const UnifiedPreferencesWizard = ({
  mode = 'edit',
  profile = null,
  onComplete,
  onCancel,
  isPremiumOrTrial = false,
  user = null,
  existingProfileCount = 0,
}: {
  mode?: 'edit' | 'onboarding';
  profile?: any;
  onComplete?: () => void;
  onCancel?: () => void;
  isPremiumOrTrial?: boolean;
  user?: any;
  existingProfileCount?: number;
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specialtyConfig, setSpecialtyConfig] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const starterPacksEnabled = useFeatureFlag('enable_starter_packs');

  // Starter pack application — pre-fills subspecialties, topics, journals
  const applyStarterPack = (specialty: any) => {
    if (!starterPacksEnabled || !specialty || mode === 'edit') return false;
    const pack = getStarterPack(specialty.id);
    if (!pack) return false;

    // Pre-fill subspecialties
    const subs = (specialty.subspecialties || []).filter(
      (s: any) => pack.subspecialtyIds.includes(s.id)
    );
    setSelectedSubspecialties(subs);

    // Pre-fill topics and journals
    setSelectedTopics(pack.topics);
    setSelectedJournals(pack.journals);

    toast.success(`${pack.label} applied! You can customize any selection.`);
    return true;
  };

  // Form state
  const [name, setName] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedSubspecialties, setSelectedSubspecialties] = useState([]);
  const [selectedJournals, setSelectedJournals] = useState([]);
  const [customJournals, setCustomJournals] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [customTopics, setCustomTopics] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [intervalValue, setIntervalValue] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState('days');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeLocal, setTimeLocal] = useState('12:00');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  
  // Time picker decomposed state (derived from timeLocal)
  const parsed12h = parseTimeTo12h(timeLocal);
  const [pickerHour, setPickerHour] = useState(parsed12h.hour);
  const [pickerMinute, setPickerMinute] = useState(parsed12h.minute);
  const [pickerAmpm, setPickerAmpm] = useState(parsed12h.ampm);
  const [maxArticles, setMaxArticles] = useState(10);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [suppressDays, setSuppressDays] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [journalNotes, setJournalNotes] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  
  // Completion step state
  const [setupComplete, setSetupComplete] = useState(false);
  const [runningDigest, setRunningDigest] = useState(false);
  const [digestResult, setDigestResult] = useState<{ digest_id?: string; article_count?: number } | null>(null);
  const [hasLibraryItems, setHasLibraryItems] = useState(false);

  // Load specialty config and populate form if editing
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const config = await configAPI.getSpecialties();
      setSpecialtyConfig(config);

      if (profile) {
        populateFromProfile(profile, config);
      } else if (mode === 'onboarding') {
        // For onboarding, set is_primary to true by default
        setIsPrimary(true);
        // Track onboarding started
        trackOnboarding('started');
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const populateFromProfile = (p, config) => {
    setName(p.name || '');
    setIsPrimary(p.is_primary || false);

    // Find specialty
    const specialty = config?.specialties?.find(s => s.id === p.specialty_id);
    if (specialty) {
      setSelectedSpecialty(specialty);

      // Handle subspecialties (support both array and single)
      if (p.subspecialties && p.subspecialties.length > 0) {
        const subspecs = p.subspecialties
          .map(id => specialty.subspecialties?.find(sub => sub.id === id))
          .filter(Boolean);
        setSelectedSubspecialties(subspecs);
      } else if (p.subspecialty_id) {
        const subspecialty = specialty.subspecialties?.find(sub => sub.id === p.subspecialty_id);
        if (subspecialty) setSelectedSubspecialties([subspecialty]);
      }
    }

    // Journals
    setSelectedJournals(p.journals_selected || []);
    setCustomJournals((p.custom_journals || []).join(', '));

    // Topics
    setSelectedTopics(p.topics_selected || p.custom_keywords || []);
    setCustomTopics((p.custom_topics || []).join(', '));

    // Schedule
    const freq = p.digest_frequency || 'daily';
    setFrequency(freq);
    // Parse custom interval from frequency string (e.g., "every_2_days", "every_3_weeks")
    const customMatch = freq.match(/^every_(\d+)_(days|weeks)$/);
    if (customMatch) {
      setIntervalValue(parseInt(customMatch[1], 10));
      setIntervalUnit(customMatch[2]);
    } else if (freq === 'daily') {
      setIntervalValue(1); setIntervalUnit('days');
    } else if (freq === 'weekly') {
      setIntervalValue(1); setIntervalUnit('weeks');
    } else if (freq === 'biweekly') {
      setIntervalValue(2); setIntervalUnit('weeks');
    } else if (freq === 'monthly') {
      setIntervalValue(30); setIntervalUnit('days');
    }
    if (p.delivery_time) {
      setTimeLocal(p.delivery_time);
      const p12 = parseTimeTo12h(p.delivery_time);
      setPickerHour(p12.hour); setPickerMinute(p12.minute); setPickerAmpm(p12.ampm);
    }
    if (p.schedule_timezone) setTimezone(p.schedule_timezone);
    if (p.day_of_week) setDayOfWeek(p.day_of_week);
    if (p.day_of_month) setDayOfMonth(p.day_of_month);

    // Article limits
    setMaxArticles(p.max_articles_per_digest || 10);

    // Email
    setEmailNotificationsEnabled(p.email_notifications_enabled !== false);

    // Suppress
    if (p.email_suppress_until) {
      const suppressDate = new Date(p.email_suppress_until);
      const now = new Date();
      const daysRemaining = Math.ceil((suppressDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysRemaining > 0) {
        const match = SUPPRESS_OPTIONS.find(opt => opt.value === String(daysRemaining));
        setSuppressDays(match ? match.value : String(daysRemaining));
      }
    }

    // Advanced
    if (p.advanced_preferences) {
      setClinicalNotes(p.advanced_preferences.clinical_notes || '');
      setJournalNotes(p.advanced_preferences.journal_notes || '');
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!selectedSpecialty;
      case 2: return true; // Subspecialties optional
      case 3: return true; // Journals optional
      case 4: return true; // Topics optional
      case 5: return true; // Schedule has defaults
      case 6: return true; // Completion step
      default: return true;
    }
  };

  const handleNext = async () => {
    if (step < TOTAL_STEPS - 1) {
      setStep(step + 1);
    } else if (step === TOTAL_STEPS - 1) {
      // Step 5 (Schedule) -> Save and move to completion
      await handleSave();
    } else {
      // Step 6 (Complete) -> Navigate away (handled by action buttons)
      if (onComplete) {
        onComplete();
      } else if (mode === 'onboarding') {
        navigate('/search');
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else if (onCancel) {
      onCancel();
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Parse custom inputs
      const customTopicsArray = customTopics
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);
      const customJournalsArray = customJournals
        .split(',')
        .map(j => j.trim())
        .filter(Boolean);

      // Build suppress_until date
      let emailSuppressUntil = null;
      if (suppressDays && parseInt(suppressDays) > 0) {
        const suppressDate = new Date();
        suppressDate.setDate(suppressDate.getDate() + parseInt(suppressDays));
        emailSuppressUntil = suppressDate.toISOString();
      }

      // Parse time
      const [hour, minute] = timeLocal.split(':').map(Number);

      // Build profile name if not provided (for both onboarding and edit/create)
      let profileName = name.trim();
      if (!profileName) {
        profileName = selectedSpecialty?.label || 'My Digest';
        if (selectedSubspecialties.length > 0) {
          profileName += ` - ${selectedSubspecialties[0].label}`;
        }
      }

      const payload = {
        name: profileName,
        specialty_id: selectedSpecialty.id,
        subspecialty_id: selectedSubspecialties[0]?.id || null,
        subspecialties: selectedSubspecialties.map(s => s.id).slice(0, MAX_SUBSPECIALTIES),
        topics_selected: selectedTopics,
        custom_topics: customTopicsArray,
        custom_keywords: [...selectedTopics, ...customTopicsArray],
        journals_selected: selectedJournals,
        custom_journals: customJournalsArray,
        max_articles_per_digest: maxArticles,
        schedule: {
          frequency,
          day_of_week: frequency === 'weekly' || frequency === 'biweekly' ? dayOfWeek : null,
          day_of_month: frequency === 'monthly' ? dayOfMonth : null,
          hour,
          minute,
          time_local: timeLocal,
          timezone,
        },
        email_notifications_enabled: emailNotificationsEnabled,
        email_suppress_until: emailSuppressUntil,
        advanced_preferences: (clinicalNotes || journalNotes) ? {
          clinical_notes: clinicalNotes || null,
          journal_notes: journalNotes || null,
        } : null,
        is_primary: isPrimary,
      };

      if (profile?.profile_id && profile.profile_id !== 'legacy') {
        // Update existing profile
        await preferencesAPI.updateProfile(profile.profile_id, payload);
        toast.success('Profile updated successfully');
        
        // For onboarding mode, still show completion step
        if (mode === 'onboarding') {
          try {
            const libraryResponse = await libraryAPI.getLibrary({ limit: 1 });
            setHasLibraryItems((libraryResponse.total || 0) > 0);
          } catch {
            setHasLibraryItems(false);
          }
          setSetupComplete(true);
          setStep(TOTAL_STEPS); // Move to completion step
        } else if (onComplete) {
          onComplete();
        }
      } else if (profile?.profile_id === 'legacy') {
        // Update legacy preferences (digest profiles feature disabled)
        const [hour, minute] = timeLocal.split(':').map(Number);
        const legacyPayload = {
          specialty_id: selectedSpecialty.id,
          subspecialty_id: selectedSubspecialties[0]?.id || null,
          subspecialties: selectedSubspecialties.map(s => s.id),
          topics_selected: selectedTopics,
          custom_topics: customTopicsArray,
          custom_keywords: [...selectedTopics, ...customTopicsArray],
          journals_selected: selectedJournals,
          custom_journals: customJournalsArray,
          digest_frequency: getEffectiveFrequency(),
          max_articles_per_digest: maxArticles,
          email_notifications_enabled: emailNotificationsEnabled,
          schedule: {
            frequency: frequency as 'daily' | 'weekly' | 'biweekly' | 'monthly',
            time_local: timeLocal,
            timezone,
            day_of_week: frequency === 'weekly' || frequency === 'biweekly' ? dayOfWeek : null,
            day_of_month: frequency === 'monthly' ? dayOfMonth : null,
          },
        };
        
        await preferencesAPI.createOrUpdatePreferences(legacyPayload);
        toast.success('Preferences saved successfully');
        
        // For onboarding mode, show completion step
        if (mode === 'onboarding') {
          try {
            const libraryResponse = await libraryAPI.getLibrary({ limit: 1 });
            setHasLibraryItems((libraryResponse.total || 0) > 0);
          } catch {
            setHasLibraryItems(false);
          }
          setSetupComplete(true);
          setStep(TOTAL_STEPS);
          // Track onboarding completion
          trackOnboarding('completed');
        } else if (onComplete) {
          onComplete();
        }
      } else {
        // Create new
        await preferencesAPI.createProfile(payload);
        
        // For onboarding mode, move to completion step
        if (mode === 'onboarding') {
          // Check if user has library items
          try {
            const libraryResponse = await libraryAPI.getLibrary({ limit: 1 });
            setHasLibraryItems((libraryResponse.total || 0) > 0);
          } catch {
            setHasLibraryItems(false);
          }
          setSetupComplete(true);
          setStep(TOTAL_STEPS); // Move to completion step
          toast.success('Your literature queue is set up!');
          // Track onboarding completion
          trackOnboarding('completed');
        } else {
          toast.success('Profile created successfully');
          if (onComplete) {
            onComplete();
          }
        }
      }
    } catch (error: any) {
      console.error('Error saving profile:', error);
      const detail = error.response?.data?.detail;
      
      // Handle case where digest profiles feature is disabled - fall back to legacy preferences
      if (detail?.error_code === 'feature_disabled') {
        try {
          // Build legacy preferences payload
          const [hour, minute] = timeLocal.split(':').map(Number);
          const legacyPayload = {
            specialty_id: selectedSpecialty.id,
            subspecialty_id: selectedSubspecialties[0]?.id || null,
            subspecialties: selectedSubspecialties.map(s => s.id),
            topics_selected: selectedTopics,
            custom_topics: customTopics.split(',').map(t => t.trim()).filter(Boolean),
            custom_keywords: [...selectedTopics, ...customTopics.split(',').map(t => t.trim()).filter(Boolean)],
            journals_selected: selectedJournals,
            custom_journals: customJournals.split(',').map(j => j.trim()).filter(Boolean),
            digest_frequency: getEffectiveFrequency(),
            max_articles_per_digest: maxArticles,
            email_notifications_enabled: emailNotificationsEnabled,
            schedule: {
              frequency: frequency as 'daily' | 'weekly' | 'biweekly' | 'monthly',
              time_local: timeLocal,
              timezone,
              day_of_week: frequency === 'weekly' || frequency === 'biweekly' ? dayOfWeek : null,
              day_of_month: frequency === 'monthly' ? dayOfMonth : null,
            },
          };
          
          await preferencesAPI.createOrUpdatePreferences(legacyPayload);
          toast.success('Preferences saved successfully');
          
          // For onboarding mode, still show completion step
          if (mode === 'onboarding') {
            try {
              const libraryResponse = await libraryAPI.getLibrary({ limit: 1 });
              setHasLibraryItems((libraryResponse.total || 0) > 0);
            } catch {
              setHasLibraryItems(false);
            }
            setSetupComplete(true);
            setStep(TOTAL_STEPS);
          } else if (onComplete) {
            onComplete();
          }
          return; // Success via legacy path
        } catch (legacyError) {
          console.error('Legacy save also failed:', legacyError);
          toast.error('Failed to save preferences');
        }
      } else if (detail?.error_code === 'phi_detected') {
        toast.error('Your input contains protected health information. Please remove any patient-related data.');
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as an array of objects
        const firstMsg = detail[0]?.msg || 'Validation error';
        toast.error(firstMsg);
      } else {
        const message = (typeof detail === 'string') ? detail : detail?.message || 'Failed to save profile';
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  // Run Digest Now for first-time users
  const handleRunDigestNow = async () => {
    try {
      setRunningDigest(true);
      const result = await digestsAPI.runDigestNow(false);
      setDigestResult(result);
      toast.success(`Digest created with ${result.article_count || 0} articles!`);
      // Track first digest run
      trackOnboarding('first_digest');
    } catch (error: any) {
      console.error('Error running digest:', error);
      const detail = error.response?.data?.detail;
      if (detail?.error_code === 'run_now_quota_exceeded') {
        toast.error('You have reached your daily queue limit. Please try again later.');
      } else {
        toast.error('Failed to run queue. Please try again.');
      }
    } finally {
      setRunningDigest(false);
    }
  };

  const toggleSubspecialty = (sub) => {
    setSelectedSubspecialties(prev => {
      const exists = prev.some(s => s.id === sub.id);
      if (exists) {
        return prev.filter(s => s.id !== sub.id);
      } else {
        if (prev.length >= MAX_SUBSPECIALTIES) {
          toast.error(`Maximum ${MAX_SUBSPECIALTIES} subspecialties allowed`);
          return prev;
        }
        return [...prev, sub];
      }
    });
  };

  const toggleJournal = (journalId) => {
    setSelectedJournals(prev =>
      prev.includes(journalId)
        ? prev.filter(j => j !== journalId)
        : [...prev, journalId]
    );
  };

  const toggleTopic = (topicId) => {
    setSelectedTopics(prev =>
      prev.includes(topicId)
        ? prev.filter(t => t !== topicId)
        : [...prev, topicId]
    );
  };

  // Sync time picker → timeLocal
  const updateTimeLocal = (h: number, m: string, ap: string) => {
    setPickerHour(h);
    setPickerMinute(m);
    setPickerAmpm(ap);
    setTimeLocal(to24h(h, m, ap));
  };

  // Derive effective frequency from custom interval for saving
  const getEffectiveFrequency = () => {
    if (intervalUnit === 'days' && intervalValue === 1) return 'daily';
    if (intervalUnit === 'weeks' && intervalValue === 1) return 'weekly';
    if (intervalUnit === 'weeks' && intervalValue === 2) return 'biweekly';
    if (intervalUnit === 'days' && intervalValue === 30) return 'monthly';
    return `every_${intervalValue}_${intervalUnit}`;
  };


  // Get available options based on selected specialty
  const availableSubspecialties = (selectedSpecialty?.subspecialties || []).filter(s => !s.deprecated);

  // Derive topics from ALL subspecialties of selected specialty (max 15)
  // Topics live in each subspecialty's "topics" array
  const availableTopics = useMemo(() => {
    if (!selectedSpecialty?.subspecialties) return [];

    const topicSet = new Set<string>();
    // Prioritize topics from selected subspecialties first
    for (const sub of selectedSubspecialties) {
      const fullSub = selectedSpecialty.subspecialties.find((s: any) => s.id === sub.id);
      if (fullSub) {
        for (const t of (fullSub.topics || [])) {
          topicSet.add(t);
        }
      }
    }
    // Then add from all other subspecialties
    for (const sub of selectedSpecialty.subspecialties) {
      for (const t of (sub.topics || [])) {
        topicSet.add(t);
      }
    }
    // Cap at 15 and return as {id, label} objects
    return Array.from(topicSet).slice(0, 15).map(t => ({ id: t, label: t }));
  }, [selectedSpecialty, selectedSubspecialties]);

  // Derive journal list from subspecialty top_journals data
  // Priority: up to 10 from selected subspecialties + up to 5 from specialty-wide (max 15)
  const availableJournals = useMemo(() => {
    if (!selectedSpecialty?.subspecialties) return [];

    // Collect all unique journals across ALL subspecialties (specialty-level pool)
    const allJournalSet = new Set<string>();
    for (const sub of selectedSpecialty.subspecialties) {
      for (const j of (sub.top_journals || [])) {
        allJournalSet.add(j);
      }
    }
    const allSpecialtyJournals = Array.from(allJournalSet);

    // Collect journals from SELECTED subspecialties only
    const selectedSubJournalSet = new Set<string>();
    if (selectedSubspecialties.length > 0) {
      for (const sub of selectedSubspecialties) {
        const fullSub = selectedSpecialty.subspecialties.find((s: any) => s.id === sub.id);
        if (fullSub) {
          for (const j of (fullSub.top_journals || [])) {
            selectedSubJournalSet.add(j);
          }
        }
      }
    }

    let finalJournals: string[];

    if (selectedSubspecialties.length > 0 && selectedSubJournalSet.size > 0) {
      // Show up to 10 from selected subspecialties + up to 5 from specialty (no dupes)
      const subJournals = Array.from(selectedSubJournalSet).slice(0, 10);
      const subSet = new Set(subJournals);
      const specOnly = allSpecialtyJournals.filter(j => !subSet.has(j)).slice(0, 5);
      finalJournals = [...subJournals, ...specOnly];
    } else {
      // No subspecialties selected — show up to 15 from all specialty journals
      finalJournals = allSpecialtyJournals.slice(0, 15);
    }

    return finalJournals.map(name => ({ id: name, label: name }));
  }, [selectedSpecialty, selectedSubspecialties]);

  // Get user tier info for digest limits
  const tierInfo = getUserTierLabel(user);
  const canCreateMore = existingProfileCount < tierInfo.limit;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="unified-preferences-wizard">
      {/* Header with Welcome Message */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {mode === 'onboarding' ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}! 👋
              </h1>
              <p className="text-gray-600 mt-2">
                Let's set up your personalized literature queue.
              </p>
              <div className="mt-3 p-3 bg-gradient-to-r from-sky-50 to-blue-50 rounded-lg border border-sky-100">
                <p className="text-sm text-sky-800">
                  <span className="font-semibold">As a {tierInfo.label} user</span>, you can set up to{' '}
                  <span className="font-bold text-sky-700">{tierInfo.limit}</span> scheduled literature queue{tierInfo.limit > 1 ? 's' : ''}.
                </p>
                <p className="text-sm text-sky-700 mt-1">
                  Please select the specialties and subspecialties of your interest.
                </p>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900">
                {profile ? 'Edit Profile' : 'Create Profile'}
              </h1>
              <p className="text-gray-500 mt-1">
                Customize your queue preferences
              </p>
              {!profile && (
                <p className="text-xs text-gray-400 mt-1">
                  Digest {existingProfileCount + 1} of {tierInfo.limit}
                </p>
              )}
            </>
          )}
        </div>
        {mode !== 'onboarding' && onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Progress Stepper */}
      <div className="flex items-center justify-between px-2 mb-8">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                i + 1 < step
                  ? 'bg-green-600 text-white'
                  : i + 1 === step
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {i + 1 < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`ml-2 text-sm hidden sm:inline ${i + 1 <= step ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {label}
            </span>
            {i < TOTAL_STEPS - 1 && (
              <div className={`w-8 sm:w-12 h-0.5 mx-2 ${i + 1 < step ? 'bg-green-600' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {/* Step 1: Specialty */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="flex items-center gap-2 text-base font-semibold mb-1">
                  <Stethoscope className="h-5 w-5 text-blue-600" />
                  Select Your Medical Specialty
                </Label>
                <p className="text-sm text-gray-500 mb-4">
                  Choose the specialty that best matches your practice area.
                </p>
                {/* Specialty Chips - Compact chip layout like subspecialties */}
                <div className="flex flex-wrap gap-2">
                  {specialtyConfig?.specialties?.filter(spec => !spec.deprecated).map((spec) => {
                    const isSelected = selectedSpecialty?.id === spec.id;
                    return (
                      <button
                        key={spec.id}
                        onClick={() => {
                          setSelectedSpecialty(spec);
                          setSelectedSubspecialties([]);
                          setSelectedJournals([]);
                          setSelectedTopics([]);
                        }}
                        className={`px-4 py-2 text-sm rounded-full border-2 transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                        }`}
                        data-testid={`specialty-${spec.id}`}
                      >
                        {spec.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quick Start — flag-gated, new profiles only */}
              {starterPacksEnabled && mode !== 'edit' && selectedSpecialty && hasStarterPack(selectedSpecialty.id) && (
                <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded-xl" data-testid="starter-pack-prompt">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Sparkles className="w-4 h-4 text-sky-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-sky-800">
                          {getStarterPack(selectedSpecialty.id)?.label}
                        </p>
                        <p className="text-xs text-sky-600 truncate">
                          {getStarterPack(selectedSpecialty.id)?.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => applyStarterPack(selectedSpecialty)}
                      className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
                      data-testid="apply-starter-pack"
                    >
                      Quick Start
                    </button>
                  </div>
                  <p className="text-[11px] text-sky-500 mt-1.5">
                    Pre-fills recommended subspecialties, topics, and journals. You can customize everything.
                  </p>
                </div>
              )}

              {/* Profile Name (for edit mode) */}
              {mode === 'edit' && (
                <div className="pt-4 border-t">
                  <Label htmlFor="profile-name">Profile Name</Label>
                  <Input
                    id="profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Cardiology Weekly, Emergency Daily"
                    className="mt-1"
                    data-testid="profile-name-input"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2: Subspecialties */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <Label className="flex items-center gap-2 text-base font-semibold mb-1">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Select Subspecialties (Optional)
                </Label>
                <p className="text-sm text-gray-500 mb-4">
                  Focus your queue on specific subspecialties. Select up to {MAX_SUBSPECIALTIES}.
                </p>
                
                {availableSubspecialties.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {availableSubspecialties.map((sub) => {
                      const selected = selectedSubspecialties.some(s => s.id === sub.id);
                      return (
                        <button
                          key={sub.id}
                          onClick={() => toggleSubspecialty(sub)}
                          className={`px-4 py-2 text-sm rounded-full border-2 transition-all ${
                            selected
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                          data-testid={`subspecialty-${sub.id}`}
                        >
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No subspecialties available for {selectedSpecialty?.label || 'this specialty'}.
                  </div>
                )}

                {selectedSubspecialties.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Selected ({selectedSubspecialties.length}/{MAX_SUBSPECIALTIES}):</strong>{' '}
                      {selectedSubspecialties.map(s => s.label).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Journals */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <Label className="flex items-center gap-2 text-base font-semibold mb-1">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  Select Top Journals
                </Label>
                <p className="text-sm text-gray-500 mb-4">
                  Prioritize articles from your preferred journals.
                </p>
                
                {availableJournals.length > 0 ? (
                  <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                    {availableJournals.map((journal) => {
                      const selected = selectedJournals.includes(journal.id);
                      return (
                        <button
                          key={journal.id}
                          onClick={() => toggleJournal(journal.id)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                            selected
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                          data-testid={`journal-${journal.id}`}
                        >
                          {journal.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No curated journals for this specialty. Add custom journals below.
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="custom-journals">Custom Journals</Label>
                <p className="text-sm text-gray-500 mb-1">
                  Add other journals by name (comma-separated)
                </p>
                <Input
                  id="custom-journals"
                  value={customJournals}
                  onChange={(e) => setCustomJournals(e.target.value)}
                  placeholder="e.g., Circulation, JACC, European Heart Journal"
                  data-testid="custom-journals-input"
                />
              </div>
            </div>
          )}

          {/* Step 4: Topics */}
          {step === 4 && (
            <div className="space-y-6">
              <div>
                <Label className="flex items-center gap-2 text-base font-semibold mb-1">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Select Topics of Interest
                </Label>
                <p className="text-sm text-gray-500 mb-4">
                  Choose topics to focus your queue on specific areas.
                </p>
                
                {availableTopics.length > 0 ? (
                  <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                    {availableTopics.map((topic) => {
                      const selected = selectedTopics.includes(topic.id);
                      return (
                        <button
                          key={topic.id}
                          onClick={() => toggleTopic(topic.id)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                            selected
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                          data-testid={`topic-${topic.id}`}
                        >
                          {topic.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No curated topics for this specialty. Add custom topics below.
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="custom-topics">Custom Topics / Keywords</Label>
                <p className="text-sm text-gray-500 mb-1">
                  Add specific conditions, treatments, or terms (comma-separated)
                </p>
                <Input
                  id="custom-topics"
                  value={customTopics}
                  onChange={(e) => setCustomTopics(e.target.value)}
                  placeholder="e.g., TAVR, heart failure, SGLT2 inhibitors"
                  data-testid="custom-topics-input"
                />
              </div>
            </div>
          )}

          {/* Step 5: Schedule */}
          {step === 5 && (
            <div className="space-y-6">
              <div>
                <Label className="flex items-center gap-2 text-base font-semibold mb-3">
                  <Clock className="h-5 w-5 text-blue-600" />
                  Delivery Schedule
                </Label>

                <div className="space-y-4">
                  {/* Custom Frequency: Every [X] [days/weeks] */}
                  <div>
                    <Label className="text-sm font-medium">Frequency</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-gray-600">Every</span>
                      <select
                        value={intervalValue}
                        onChange={(e) => setIntervalValue(parseInt(e.target.value, 10))}
                        className="w-20 px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="interval-value-select"
                      >
                        {INTERVAL_VALUES.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                      <select
                        value={intervalUnit}
                        onChange={(e) => setIntervalUnit(e.target.value)}
                        className="w-28 px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="interval-unit-select"
                      >
                        {INTERVAL_UNITS.map(u => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {getEffectiveFrequency() === 'daily' && 'You will receive a new queue every day.'}
                      {getEffectiveFrequency() === 'weekly' && 'You will receive a new queue once a week.'}
                      {getEffectiveFrequency() === 'biweekly' && 'You will receive a new queue every two weeks.'}
                      {!['daily', 'weekly', 'biweekly'].includes(getEffectiveFrequency()) && 
                        `You will receive a new queue every ${intervalValue} ${intervalUnit}.`}
                    </p>
                  </div>

                  {/* Day of Week (for weekly intervals) */}
                  {intervalUnit === 'weeks' && (
                    <div>
                      <Label htmlFor="day-of-week" className="text-sm">Preferred Day</Label>
                      <select
                        id="day-of-week"
                        value={dayOfWeek}
                        onChange={(e) => setDayOfWeek(e.target.value)}
                        className="w-full mt-1 px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="day-of-week-select"
                      >
                        {DAYS_OF_WEEK.map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Full Time Picker with Hour / AM-PM (simplified) */}
                  <div>
                    <Label className="text-sm font-medium">Delivery Time</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <select
                        value={pickerHour}
                        onChange={(e) => updateTimeLocal(parseInt(e.target.value, 10), '00', pickerAmpm)}
                        className="w-20 px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        data-testid="time-hour-select"
                      >
                        {HOURS_12.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                      <div className="flex rounded-md border overflow-hidden">
                        {AM_PM.map(ap => (
                          <button
                            key={ap}
                            type="button"
                            onClick={() => updateTimeLocal(pickerHour, '00', ap)}
                            className={`px-3 py-2 text-sm font-medium transition-colors ${
                              pickerAmpm === ap
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            data-testid={`time-${ap.toLowerCase()}-btn`}
                          >
                            {ap}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Timezone: {timezone}
                    </p>
                  </div>

                  {/* Max Articles */}
                  <div>
                    <Label htmlFor="max-articles" className="text-sm">Articles per Queue (5-20)</Label>
                    <Input
                      id="max-articles"
                      type="number"
                      min={5}
                      max={20}
                      value={maxArticles}
                      onChange={(e) => setMaxArticles(parseInt(e.target.value))}
                      className="mt-1"
                      data-testid="max-articles-input"
                    />
                  </div>
                </div>
              </div>

              {/* Email Notifications */}
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="email-notifications"
                  checked={emailNotificationsEnabled}
                  onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="email-notifications-checkbox"
                />
                <Label htmlFor="email-notifications" className="text-sm cursor-pointer">
                  Send email notifications for this queue
                </Label>
              </div>

              {/* Primary Profile Toggle - Show for edit mode, or onboarding when creating additional profile (not first profile and not editing existing) */}
              {(mode === 'edit' || (mode === 'onboarding' && !profile && existingProfileCount > 0)) && (
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="is-primary"
                    checked={isPrimary}
                    onChange={(e) => setIsPrimary(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="is-primary-checkbox"
                  />
                  <Label htmlFor="is-primary" className="text-sm cursor-pointer flex items-center gap-1">
                    <Star className="h-4 w-4 text-amber-500" />
                    Set as primary queue
                  </Label>
                </div>
              )}
              
              {/* Show primary info for first digest in onboarding (only when creating first profile, not editing) */}
              {mode === 'onboarding' && existingProfileCount === 0 && !profile && (
                <div className="flex items-center gap-2 pt-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  <span>This will be set as your primary queue.</span>
                </div>
              )}

              {/* Advanced Settings Accordion */}
              <div className="border rounded-lg mt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left"
                  data-testid="advanced-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Settings className="h-4 w-4" />
                    Advanced Settings
                  </span>
                  {showAdvanced ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {showAdvanced && (
                  <div className="px-4 pb-4 space-y-4 border-t">
                    {/* Suppress Digests */}
                    <div className="pt-4">
                      <Label htmlFor="suppress-days" className="text-sm">Suppress Digests</Label>
                      <p className="text-xs text-gray-500 mb-1">
                        Temporarily pause email delivery
                      </p>
                      <select
                        id="suppress-days"
                        value={suppressDays}
                        onChange={(e) => setSuppressDays(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        data-testid="suppress-select"
                      >
                        {SUPPRESS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                        {suppressDays && !SUPPRESS_OPTIONS.find(o => o.value === suppressDays) && (
                          <option value={suppressDays}>{suppressDays} days (current)</option>
                        )}
                      </select>
                    </div>

                    {/* Clinical Notes */}
                    <div>
                      <Label htmlFor="clinical-notes" className="text-sm">Clinical Notes</Label>
                      <p className="text-xs text-gray-500 mb-1">
                        Special clinical focus or patient population notes
                      </p>
                      <Textarea
                        id="clinical-notes"
                        value={clinicalNotes}
                        onChange={(e) => setClinicalNotes(e.target.value)}
                        placeholder="e.g., Focus on elderly patients with comorbidities"
                        rows={2}
                        data-testid="clinical-notes-input"
                      />
                    </div>

                    {/* Journal Notes */}
                    <div>
                      <Label htmlFor="journal-notes" className="text-sm">Journal Notes</Label>
                      <p className="text-xs text-gray-500 mb-1">
                        Special journal preferences or exclusions
                      </p>
                      <Textarea
                        id="journal-notes"
                        value={journalNotes}
                        onChange={(e) => setJournalNotes(e.target.value)}
                        placeholder="e.g., Prefer open access journals"
                        rows={2}
                        data-testid="journal-notes-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 6: Completion */}
          {step === 6 && mode === 'onboarding' && (
            <div className="space-y-6 text-center" data-testid="completion-step">
              {/* Success Icon */}
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <PartyPopper className="w-10 h-10 text-green-600" />
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900">You're All Set!</h2>
                <p className="text-gray-500 mt-2">Your personalized literature queue is configured and ready to go.</p>
              </div>

              {/* Summary Card */}
              <div className="text-left bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  Your Digest Summary
                </h3>
                
                <div className="grid gap-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Stethoscope className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="font-medium">Specialty:</span>{' '}
                      <span className="text-gray-600">{selectedSpecialty?.label || 'Not selected'}</span>
                      {selectedSubspecialties.length > 0 && (
                        <span className="text-gray-500"> ({selectedSubspecialties.map(s => s.label).join(', ')})</span>
                      )}
                    </div>
                  </div>
                  
                  {(selectedTopics.length > 0 || customTopics) && (
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <span className="font-medium">Topics:</span>{' '}
                        <span className="text-gray-600">
                          {[...selectedTopics, ...(customTopics ? customTopics.split(',').map(t => t.trim()).filter(Boolean) : [])].slice(0, 5).join(', ')}
                          {(selectedTopics.length + (customTopics ? customTopics.split(',').filter(Boolean).length : 0)) > 5 && '...'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="font-medium">Schedule:</span>{' '}
                      <span className="text-gray-600">
                        {frequency === 'daily' ? 'Daily' : 
                         frequency === 'weekly' ? `Every ${dayOfWeek}` :
                         frequency === 'biweekly' ? `Every other ${dayOfWeek}` :
                         `Monthly on day ${dayOfMonth}`} at {timeLocal}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <span className="font-medium">Email:</span>{' '}
                      <span className="text-gray-600">{emailNotificationsEnabled ? 'Notifications enabled' : 'Notifications disabled'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* What Happens Next */}
              <div className="text-left bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
                <ul className="text-sm text-blue-800 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">1.</span>
                    <span><strong>Search</strong> — Find new research and save to your LitHub</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">2.</span>
                    <span><strong>Screen</strong> — Triage queue articles when they arrive</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">3.</span>
                    <span><strong>Learn</strong> — Read summaries, listen to audio, and discuss with peers</span>
                  </li>
                </ul>
              </div>

              {/* Run Digest Now */}
              {!digestResult && (
                <div className="bg-amber-50 rounded-lg p-4 text-left">
                  <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Get Started Immediately
                  </h3>
                  <p className="text-sm text-amber-800 mb-3">
                    Don't want to wait? Generate your first batch of articles now!
                  </p>
                  <Button
                    onClick={handleRunDigestNow}
                    disabled={runningDigest}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="run-digest-now-btn"
                  >
                    {runningDigest ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating articles...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Run My First Digest Now
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Digest Generated */}
              {digestResult && (
                <div className="bg-green-50 rounded-lg p-4 text-left">
                  <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Your First Digest is Ready!
                  </h3>
                  <p className="text-sm text-green-800">
                    {digestResult.article_count || 0} articles are waiting for you to screen.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  onClick={() => navigate('/search')}
                  variant={!hasLibraryItems && !digestResult ? 'default' : 'outline'}
                  className="flex-1"
                  data-testid="go-to-search-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search for Articles
                </Button>
                
                <Button
                  onClick={() => navigate('/library')}
                  variant={hasLibraryItems ? 'default' : 'outline'}
                  className="flex-1"
                  data-testid="go-to-learn-btn"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Go to Learn
                </Button>
                
                {digestResult && (digestResult.article_count || 0) > 0 && (
                  <Button
                    onClick={() => navigate('/screen')}
                    className="flex-1"
                    data-testid="go-to-screen-btn"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Screen Articles ({digestResult.article_count})
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons - Hide on completion step for onboarding */}
      {!(step === 6 && mode === 'onboarding') && (
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={saving || runningDigest}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step === 1 ? (mode === 'onboarding' ? 'Skip' : 'Cancel') : 'Back'}
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canProceed() || saving || runningDigest}
            data-testid="wizard-next-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : step === TOTAL_STEPS - 1 ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                Complete Setup
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default UnifiedPreferencesWizard;
